/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { createFilterOptions } from '@mui/material/Autocomplete'

import { SabrePackagingType, SabrePackagingUnits } from '@dai/graphql'

import type { ProductDetails } from './labelingHelpers'
import type {
  CarouselImage,
  ProductFormMode,
  ProductFormValues,
} from './ProductDialog.types'

export const PACKAGING_UNIT_OPTIONS = Object.values(SabrePackagingUnits)
export const PACKAGING_CONTAINER_OPTIONS = Object.values(SabrePackagingType)

export const stringAutocompleteFilter = createFilterOptions<string>({
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
})

export const formatEnumLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

export const normalizeSearchTerm = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')

export const getStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }

  if (value && typeof value === 'object') {
    if ('name' in value && typeof value.name === 'string') {
      return value.name
    }
    if ('category' in value && typeof value.category === 'string') {
      return value.category
    }
  }

  return ''
}

export const mergeUniqueOptions = (options: string[], preferred?: string) => {
  const next = new Map<string, string>()

  if (preferred?.trim()) {
    next.set(preferred.trim().toLowerCase(), preferred.trim())
  }

  options.forEach(option => {
    const trimmed = option.trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (!next.has(key)) {
      next.set(key, trimmed)
    }
  })

  return Array.from(next.values())
}

export const getDefaultImageSearchQuery = (
  product: ProductDetails | null,
  fallbackName: string,
) => {
  const candidate = (
    product?.name ||
    getStringValue(product?.commonName) ||
    fallbackName
  ).trim()
  if (!candidate) return ''
  if (/^unknown product\b/i.test(candidate)) {
    return ''
  }
  return candidate
}

export const getInitialValues = (
  mode: ProductFormMode,
  product: ProductDetails | null,
): ProductFormValues =>
  mode === 'edit'
    ? {
        upc: product?.upcA ?? '',
        name: product?.name ?? '',
        commonName: getStringValue(product?.commonName),
        category: product?.category?.name ?? '',
        brand: product?.brand?.name ?? '',
        packagingQuantity:
          product?.packaging?.quantity != null
            ? String(product.packaging.quantity)
            : '',
        packagingSize:
          product?.packaging?.size != null
            ? String(product.packaging.size)
            : '',
        packagingUnit: product?.packaging?.unit ?? '',
        packagingContainer: product?.packaging?.container ?? '',
      }
    : {
        upc: '',
        name: '',
        commonName: '',
        category: '',
        brand: '',
        packagingQuantity: '',
        packagingSize: '',
        packagingUnit: '',
        packagingContainer: '',
      }

export const getApolloErrorMessage = (error: unknown, fallback: string) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'graphQLErrors' in error &&
    Array.isArray(
      (error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors,
    ) &&
    (error as { graphQLErrors: Array<{ message?: string }> }).graphQLErrors[0]
      ?.message
  ) {
    return (
      (error as { graphQLErrors: Array<{ message?: string }> }).graphQLErrors[0]
        ?.message ?? fallback
    )
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }

  return fallback
}

export const mapMarketingImages = (
  productImages:
    | Array<{
        type: string
        url?: string | null
        id: string
        isPrimary?: boolean
      }>
    | null
    | undefined,
  marketingType: string,
): CarouselImage[] =>
  (
    productImages
      ?.filter(
        image =>
          image.type === marketingType &&
          typeof image.url === 'string' &&
          image.url.trim().length > 0,
      )
      .map(image => ({
        uri: image.url!,
        id: image.id,
        isPrimary: image.isPrimary,
      })) || []
  ).sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)))
